import { useCallback, useEffect, useMemo, useState } from 'react';
import { LayoutGrid } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/layout/Sidebar';
import ConversationList from '../components/layout/ConversationList';
import ChatWindow from '../components/chat/ChatWindow';
import RightProfilePanel from '../components/chat/RightProfilePanel';
import CreateConversationModal from '../components/chat/CreateConversationModal';
import AiConfigPanel from '../components/ai/AiConfigPanel';
import ConversationFunnel from '../components/funnel/ConversationFunnel';

import { FUNNEL_STAGES, CONVERSATION_STATUS } from '../data/conversations';
import { useAiAgents } from '../hooks/useAiAgents';
import { TEAMS } from '../data/teams';
import { INITIAL_TAGS } from '../data/tags';
import { INITIAL_SLA_CONFIG } from '../data/sla';
import SettingsPanel from '../components/settings/SettingsPanel';
import ReportsPanel from '../components/reports/ReportsPanel';
import ContactsArea from '../components/contacts/ContactsArea';
import { dedupeConversationsByContactChannel } from '../utils/conversations';
import { apiFetch } from '../utils/api';
import { useUpdateContact } from '../hooks/useUpdateContact';

const teamsAsArray = Object.values(TEAMS);

function buildAvatar(name) {
  return name
    ? name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase()
    : '??';
}

function Toast({ message, type, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 5000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div
      role="alert"
      className={`fixed bottom-6 right-6 z-50 max-w-sm px-4 py-3 rounded-xl shadow-lg border ${
        type === 'error' ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'
      }`}
    >
      <p className="font-medium text-sm">{message}</p>
    </div>
  );
}

export default function App() {
  const { user: currentUser, logout } = useAuth();
  const [toast, setToast] = useState(null);
  const showToast = useCallback(({ message, type = 'error' }) => setToast({ message, type }), []);
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messagesByConversation, setMessagesByConversation] = useState({});
  const [channels, setChannels] = useState([]);
  const [channelAccounts, setChannelAccounts] = useState([]);
  const [, setLoadingData] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = useMemo(() => {
    const path = location.pathname || '/';
    if (path.startsWith('/contatos')) return 'contacts';
    if (path.startsWith('/conversas')) return 'inbox';
    if (path.startsWith('/funil')) return 'funnel';
    if (path.startsWith('/ia')) return 'ai_config';
    if (path.startsWith('/relatorios')) return 'analytics';
    if (path.startsWith('/configuracoes')) return 'settings';
    return 'inbox';
  }, [location.pathname]);
  const contactsSubTab = useMemo(() => {
    const path = location.pathname || '/';
    return path.startsWith('/contatos/empresas') ? 'companies' : 'contacts';
  }, [location.pathname]);
  const handleTabChange = useCallback((tab) => {
    const map = {
      inbox: '/inbox',
      contacts: '/contatos',
      funnel: '/funil',
      ai_config: '/ia',
      analytics: '/relatorios',
      settings: '/configuracoes',
    };
    navigate(map[tab] || '/inbox');
  }, [navigate]);
  const [messagesStatusFilter, setMessagesStatusFilter] = useState([CONVERSATION_STATUS.ATIVO]); // multiseleção, padrão: ativo
  const [messagesSearchQuery, setMessagesSearchQuery] = useState('');
  const [messagesTeamFilterId, setMessagesTeamFilterId] = useState(null);
  const [messagesChannelFilter, setMessagesChannelFilter] = useState(null);
  const [messagesTagFilterId, setMessagesTagFilterId] = useState(null);
  const [funnelSearchQuery, setFunnelSearchQuery] = useState('');
  const [funnelTeamFilterId, setFunnelTeamFilterId] = useState(null);
  const [funnelChannelFilter, setFunnelChannelFilter] = useState(null);
  const [funnelTagFilterId, setFunnelTagFilterId] = useState(null);
  const [funnelStatusFilter, setFunnelStatusFilter] = useState([CONVERSATION_STATUS.ATIVO, CONVERSATION_STATUS.AGUARDANDO]); // multiseleção, padrão: ativo + aguardando
  const [currentProfile, setCurrentProfile] = useState(() => currentUser);
  useEffect(() => {
    if (currentUser) setCurrentProfile(prev => prev?.id === currentUser.id ? prev : currentUser);
  }, [currentUser]);
  useEffect(() => {
    if (location.pathname === '/') {
      navigate('/inbox', { replace: true });
    }
  }, [location.pathname, navigate]);
  useEffect(() => {
    if (!currentUser) return;
    let active = true;
    const loadData = async () => {
      setLoadingData(true);
      try {
        const [channelsList, channelAccountsList, conversationsList, tagsList, teamsList, stagesList] = await Promise.all([
          fetchAll('/channels', {}, 100),
          fetchAll('/channel-accounts', {}, 200),
          fetchAll('/conversations'),
          fetchAll('/tags', {}, 100),
          fetchAll('/teams', {}, 100),
          fetchAll('/funnel-stages', {}, 100),
        ]);

        if (!active) return;
        setChannels(channelsList);
        setChannelAccounts(channelAccountsList);
        setTags(tagsList);
        setTeams(teamsList);
        setFunnelStages(stagesList.map(fromApiStage));

        const contactIndex = {};
        const channelIndex = channelsList.reduce((acc, ch) => {
          acc[ch.id] = ch.name || ch.label || ch.id;
          return acc;
        }, {});
        const mappedConversations = conversationsList.map((c) => mapConversation(c, contactIndex, channelIndex));
        setConversations(mappedConversations);
        if (!selectedId && mappedConversations.length) {
          setSelectedId(mappedConversations[0].id);
        }

        try {
          const usersList = await fetchAll('/users', {}, 100);
          const mappedUsers = usersList.length ? usersList.map(mapUser) : (currentUser ? [currentUser] : []);
          if (active) setUsers(mappedUsers);
        } catch (_) {
          if (active) setUsers(currentUser ? [currentUser] : []);
        }

        try {
          await fetchContactsPage({ page: 1, pageSize: 10, status: 'ativo' });
          await fetchCompaniesPage({ page: 1, pageSize: 10 });
        } catch (_) {
          // ignore
        }

        try {
          const res = await apiFetch('/sla-config');
          if (active && res?.data?.config_json) {
            setSlaConfig(res.data.config_json);
          }
        } catch (_) {
          // manter SLA local
        }
      } catch (e) {
        console.error('Erro ao carregar dados iniciais', e);
      } finally {
        if (active) setLoadingData(false);
      }
    };
    loadData();
    return () => {
      active = false;
    };
  }, [currentUser]);
  const {
    agents: aiAgents,
    selectedAgentId: aiSelectedAgentId,
    setSelectedAgentId: setAiSelectedAgentId,
    selectedAgent: aiSelectedAgent,
    createAgent,
    updateAgent,
    deleteAgent,
    addKnowledgeSource,
    removeKnowledgeSource,
    addIntegration,
    setIntegrationStatus,
    removeIntegration,
  } = useAiAgents();
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);

  const [funnelStages, setFunnelStages] = useState([]);
  const [teams, setTeams] = useState(teamsAsArray);
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState(INITIAL_TAGS);
  const [slaConfig, setSlaConfig] = useState(INITIAL_SLA_CONFIG);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [companiesTotal, setCompaniesTotal] = useState(0);
  const [showCreateConversationModal, setShowCreateConversationModal] = useState(false);

  const teamsAsObject = teams.reduce((acc, t) => {
    acc[t.id?.toUpperCase()] = t;
    return acc;
  }, {});

  const formatTime = (value) => {
    if (!value) return '';
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (/^\d{1,2}:\d{2}$/.test(trimmed)) return trimmed;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (isToday) return time;
    const day = date.toLocaleDateString('pt-BR');
    return `${day} ${time}`;
  };

  const toFrontRole = (role) => {
    const map = { OWNER: 'admin', ADMIN: 'admin', AGENT: 'agent', VIEWER: 'viewer' };
    return map[role] || 'viewer';
  };
  const toApiRole = (role) => {
    const map = { admin: 'ADMIN', agent: 'AGENT', viewer: 'VIEWER', owner: 'OWNER' };
    return map[role] || 'AGENT';
  };

  const fromApiStage = (s) => ({
    id: s.id,
    label: s.label,
    slug: s.slug || (s.label ? s.label.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-') : ''),
    color: s.color || 'bg-slate-50 border-slate-200',
    headerColor: s.header_color || 'bg-slate-100 text-slate-800',
    position: s.position ?? 0,
  });

  const toApiStage = (s) => ({
    label: s.label,
    slug: s.slug,
    color: s.color,
    header_color: s.headerColor,
    position: s.position ?? 0,
  });

  const channelsMap = channels.reduce((acc, ch) => {
    acc[ch.id] = ch.name || ch.label || ch.id;
    return acc;
  }, {});

  const contactsMap = contacts.reduce((acc, c) => {
    acc[c.id] = c;
    return acc;
  }, {});

  const apiStatusToConversationStatus = (s) => {
    if (s === 'waiting') return CONVERSATION_STATUS.AGUARDANDO;
    if (s === 'closed') return CONVERSATION_STATUS.FINALIZADO;
    return CONVERSATION_STATUS.ATIVO;
  };

  const mapConversation = (conv, contactIndex = contactsMap, channelIndex = channelsMap) => {
    const contact = contactIndex[conv.contactId ?? conv.contact_id];
    const contactData = conv.contact || contact;
    const name = conv.name ?? contactData?.name ?? contactData?.nome ?? '—';
    const avatarUrl = contactData?.avatar ?? conv.avatar;
    const isAvatarUrl = typeof avatarUrl === 'string' && (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://'));
    const avatarDisplay = isAvatarUrl ? avatarUrl : buildAvatar(name);
    const channel = conv.channel;
    const channelName = conv.source ?? (channel ? (channel.label || channel.type || channel.id) : null) ?? channelIndex[conv.channelId ?? conv.channel_id] ?? 'whatsapp';
    const stageSlugOrId = conv.stage?.slug ?? conv.stage?.id ?? conv.stageId ?? conv.stage_id ?? conv.stage ?? 'triagem';
    return {
      id: conv.id,
      contact_id: conv.contactId ?? conv.contact_id,
      channelId: conv.channelId ?? conv.channel_id ?? null,
      name,
      avatar: avatarDisplay,
      avatarUrl: isAvatarUrl ? avatarUrl : null,
      avatarLabel: buildAvatar(name),
      source: channelName,
      status: conv.status || 'offline',
      conversationStatus: conv.conversationStatus ?? apiStatusToConversationStatus(conv.status) ?? CONVERSATION_STATUS.ATIVO,
      phone: contactData?.phone ?? contactData?.telefone ?? conv.phone ?? '',
      email: contactData?.email ?? conv.email ?? '',
      teamId: conv.team_id ?? conv.teamId ?? null,
      stage: stageSlugOrId,
      assignedAiId: conv.assigned_ai_id ?? conv.assignedAiId ?? null,
      aiActive: conv.ai_active ?? conv.aiActive ?? false,
      lastMessage: conv.last_message ?? conv.lastMessage ?? '',
      unread: conv.unread_count ?? conv.unread ?? 0,
      timestamp: formatTime(conv.last_message_at ?? conv.created_at ?? conv.createdAt ?? conv.timestamp),
      created_at: conv.created_at ?? conv.createdAt ?? null,
      updated_at: conv.updated_at ?? conv.updatedAt ?? null,
      last_message_at: conv.last_message_at ?? null,
    };
  };

  const mapMessage = (msg) => ({
    id: msg.id,
    text: msg.text || '',
    sender: msg.sender || msg.sender_type || 'agent',
    isAi: msg.isAi ?? msg.is_ai ?? false,
    aiName: msg.aiName ?? msg.ai_name ?? null,
    senderName: msg.senderName ?? msg.sender_name ?? null,
    user: msg.user ?? null,
    time: formatTime(msg.time || msg.created_at || msg.sent_at),
    status: msg.status ?? null, // pending | sent | failed (Evolution: envia pro WhatsApp)
  });

  const mapUser = (u) => ({
    id: u.id,
    name: u.name,
    email: u.email,
    avatar: u.avatar || buildAvatar(u.name),
    role: toFrontRole(u.role),
    teamId: u.teamId ?? u.team_id ?? null,
  });

  const fetchAll = async (path, params = {}, pageSize = 200) => {
    let page = 1;
    let items = [];
    while (true) {
      const query = new URLSearchParams({ ...params, page: String(page), pageSize: String(pageSize) });
      const res = await apiFetch(`${path}?${query.toString()}`);
      const batch = res.items ?? (Array.isArray(res.data) ? res.data : []);
      items = [...items, ...batch];
      const total = res.total ?? res.meta?.total ?? items.length;
      if (!total || items.length >= total || batch.length < pageSize) break;
      page += 1;
      if (page > 50) break;
    }
    return items;
  };

  const fetchContactsPage = useCallback(async (filters) => {
    const {
      q = '',
      status = '',
      tag = '',
      empresa_id = '',
      canal_origem = '',
      page = 1,
      pageSize = 10,
    } = filters || {};
    const query = new URLSearchParams();
    if (q) query.set('q', q);
    if (status) query.set('status', status);
    if (tag) query.set('tag', tag);
    if (empresa_id) query.set('empresa_id', empresa_id);
    if (canal_origem) query.set('canal_origem', canal_origem);
    query.set('page', String(page));
    query.set('pageSize', String(pageSize));
    const res = await apiFetch(`/contacts?${query.toString()}`);
    const rawItems = res.items || res.data || [];
    const items = rawItems.map((c) => ({
      ...c,
      nome: c.nome ?? c.name,
      telefone: c.telefone ?? c.phone,
      tags: Array.isArray(c.tags) ? c.tags : [],
    }));
    setContacts(items);
    setContactsTotal(res.total ?? res.meta?.total ?? items.length);
    return res;
  }, []);

  const fetchCompaniesPage = useCallback(async (filters) => {
    const { q = '', status = '', page = 1, pageSize = 10 } = filters || {};
    const query = new URLSearchParams();
    if (q) query.set('q', q);
    if (status) query.set('status', status);
    query.set('page', String(page));
    query.set('pageSize', String(pageSize));
    try {
      const res = await apiFetch(`/companies?${query.toString()}`);
      const items = res.items ?? res.data ?? [];
      const total = res.total ?? res.meta?.total ?? 0;
      setCompanies(Array.isArray(items) ? items : []);
      setCompaniesTotal(Number(total) || 0);
      return res;
    } catch (e) {
      setCompanies([]);
      setCompaniesTotal(0);
      return { items: [], total: 0 };
    }
  }, []);

  // Importante: `selectedConv` precisa existir antes de qualquer hook/dep array que o referencie,
  // senão gera TDZ em runtime ("Cannot access 'selectedConv' before initialization").
  const selectedConv = conversations.find((c) => c.id === selectedId);

  /** Garante que o contato da conversa selecionada esteja no state (busca por ID se faltar) */
  useEffect(() => {
    if (activeTab !== 'inbox' || !selectedConv?.contact_id) return;
    const contactId = selectedConv.contact_id;
    if (contacts.some((c) => c.id === contactId)) return;
    let cancelled = false;
    apiFetch(`/contacts/${contactId}`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data ?? res;
        const normalized = {
          ...data,
          nome: data.nome ?? data.name,
          telefone: data.telefone ?? data.phone,
          tags: Array.isArray(data.tags) ? data.tags : [],
        };
        setContacts((prev) => (prev.some((c) => c.id === contactId) ? prev : [...prev, normalized]));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [activeTab, selectedConv?.contact_id, selectedConv?.id]);

  /** Resolve contato vinculado à conversa (por contact_id ou telefone/email) */
  const getContactForConversation = (c) => {
    if (!c) return null;
    const byId = c.contact_id && contacts.find((ct) => ct.id === c.contact_id);
    if (byId) return byId;
    if (!contacts.length) return null;
    const phoneNorm = (v) => (v || '').replace(/\D/g, '');
    const convPhone = phoneNorm(c.phone);
    const convEmail = (c.email || '').trim().toLowerCase();
    return contacts.find((ct) => (convPhone && phoneNorm(ct.telefone) === convPhone) || (convEmail && (ct.email || '').trim().toLowerCase() === convEmail)) || null;
  };

  /** Lista: status + equipe + canal + etiqueta + busca; uma conversa por contato por canal */
  const conversationsForList = dedupeConversationsByContactChannel(
    conversations.filter((c) => {
      const status = c.conversationStatus ?? CONVERSATION_STATUS.ATIVO;
      if (!messagesStatusFilter.length || !messagesStatusFilter.includes(status)) return false;
      if (messagesTeamFilterId) {
        const convTeam = (c.teamId || '').toLowerCase();
        if (convTeam !== messagesTeamFilterId.toLowerCase()) return false;
      }
      if (messagesChannelFilter) {
        const convSource = (c.source || '').toLowerCase();
        if (convSource !== messagesChannelFilter.toLowerCase()) return false;
      }
      if (messagesTagFilterId) {
        const contact = getContactForConversation(c);
        if (!contact || !(contact.tags || []).includes(messagesTagFilterId)) return false;
      }
      if (messagesSearchQuery.trim()) {
        const q = messagesSearchQuery.trim().toLowerCase();
        const name = (c.name || '').toLowerCase();
        const lastMessage = (c.lastMessage || '').toLowerCase();
        const email = (c.email || '').toLowerCase();
        const phone = (c.phone || '').replace(/\D/g, '');
        const qNorm = q.replace(/\D/g, '');
        const match = name.includes(q) || lastMessage.includes(q) || email.includes(q) || (qNorm.length >= 2 && phone.includes(qNorm));
        if (!match) return false;
      }
      return true;
    }),
    getContactForConversation
  );

  /** Funil: status + equipe + canal + etiqueta + busca; uma conversa por contato por canal */
  const conversationsForFunnel = dedupeConversationsByContactChannel(
    conversations.filter((c) => {
      const status = c.conversationStatus ?? CONVERSATION_STATUS.ATIVO;
      if (!funnelStatusFilter.length || !funnelStatusFilter.includes(status)) return false;
      if (funnelTeamFilterId) {
        const convTeam = (c.teamId || '').toLowerCase();
        if (convTeam !== funnelTeamFilterId.toLowerCase()) return false;
      }
      if (funnelChannelFilter) {
        const convSource = (c.source || '').toLowerCase();
        if (convSource !== funnelChannelFilter.toLowerCase()) return false;
      }
      if (funnelTagFilterId) {
        const contact = getContactForConversation(c);
        if (!contact || !(contact.tags || []).includes(funnelTagFilterId)) return false;
      }
      if (funnelSearchQuery.trim()) {
        const q = funnelSearchQuery.trim().toLowerCase();
        const name = (c.name || '').toLowerCase();
        const lastMessage = (c.lastMessage || '').toLowerCase();
        const email = (c.email || '').toLowerCase();
        const phone = (c.phone || '').replace(/\D/g, '');
        const qNorm = q.replace(/\D/g, '');
        const match = name.includes(q) || lastMessage.includes(q) || email.includes(q) || (qNorm.length >= 2 && phone.includes(qNorm));
        if (!match) return false;
      }
      return true;
    }),
    getContactForConversation
  );

  const selectedMessages = selectedId ? (messagesByConversation[selectedId] || []) : [];
  const selectedConvWithMessages = selectedConv
    ? { ...selectedConv, messages: selectedMessages }
    : null;

  const loadMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    try {
      const pageSize = 100;
      let page = 1;
      let allItems = [];
      let total = null;
      do {
        const res = await apiFetch(`/conversations/${conversationId}/messages?page=${page}&pageSize=${pageSize}`);
        const batch = res.items || [];
        total = res.total ?? total;
        allItems = [...allItems, ...batch];
        if (!total || batch.length < pageSize || allItems.length >= total) break;
        page += 1;
      } while (page <= 50);
      const items = allItems.map(mapMessage);
      setMessagesByConversation((prev) => ({ ...prev, [conversationId]: items }));
    } catch (e) {
      console.error('Erro ao carregar mensagens', e);
    }
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    const run = async () => {
      await loadMessages(selectedId);
    };
    run();
  }, [selectedId, loadMessages]);

  const refreshConversations = useCallback(async () => {
    try {
      const conversationsList = await fetchAll('/conversations');
      const contactIndex = contacts.reduce((acc, c) => {
        acc[c.id] = c;
        return acc;
      }, {});
      const channelIndex = channels.reduce((acc, ch) => {
        acc[ch.id] = ch.name || ch.label || ch.id;
        return acc;
      }, {});
      const mappedConversations = conversationsList.map((c) => mapConversation(c, contactIndex, channelIndex));
      setConversations(mappedConversations);
      if (selectedId && !mappedConversations.find((c) => c.id === selectedId) && mappedConversations.length) {
        setSelectedId(mappedConversations[0].id);
      }
    } catch (e) {
      console.error('Erro ao atualizar conversas', e);
    }
  }, [channels, contacts, selectedId]);

  useEffect(() => {
    if (!currentUser || activeTab !== 'inbox') return;
    const interval = setInterval(() => {
      refreshConversations();
      if (selectedId) loadMessages(selectedId);
    }, 2000);
    return () => clearInterval(interval);
  }, [activeTab, currentUser, loadMessages, refreshConversations, selectedId]);

  /** Contato vinculado à conversa: por contact_id ou por telefone/email (tags sempre array) */
  const selectedContact = (() => {
    if (!selectedConv) return null;
    let c = selectedConv.contact_id ? contacts.find((ct) => ct.id === selectedConv.contact_id) : null;
    if (!c && contacts.length) {
      const phoneNorm = (v) => (v || '').replace(/\D/g, '');
      const convPhone = phoneNorm(selectedConv.phone);
      const convEmail = (selectedConv.email || '').trim().toLowerCase();
      c = contacts.find((ct) => (convPhone && phoneNorm(ct.telefone) === convPhone) || (convEmail && (ct.email || '').trim().toLowerCase() === convEmail)) || null;
    }
    if (!c) return null;
    return { ...c, tags: Array.isArray(c.tags) ? c.tags : [] };
  })();

  const clientNonce = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/x/g, () => ((Math.random() * 16) | 0).toString(16)));

  const handleSendMessage = async (id, payload, isAi = false, aiName = null) => {
    const isTextOnly = typeof payload === 'string';
    const body = isTextOnly
      ? { text: payload, clientNonce: clientNonce() }
      : {
          ...(payload.text !== undefined && payload.text !== '' ? { text: payload.text } : {}),
          ...(payload.type ? { type: payload.type } : {}),
          ...(payload.evolutionPayload && Object.keys(payload.evolutionPayload).length > 0 ? { evolutionPayload: payload.evolutionPayload } : {}),
          clientNonce: payload.clientNonce ?? clientNonce(),
        };
    if (isTextOnly && !(payload && payload.trim())) return;
    if (!isTextOnly && !body.text && !body.type) return;
    try {
      const res = await apiFetch(`/conversations/${id}/messages`, {
        method: 'POST',
        json: body,
      });
      const raw = res.data ?? res;
      const newMessage = mapMessage(raw);
      setMessagesByConversation((prev) => ({
        ...prev,
        [id]: [...(prev[id] || []), newMessage],
      }));
      setConversations((prev) =>
        prev.map((c) =>
          c.id === id
            ? { ...c, lastMessage: newMessage.text, timestamp: newMessage.time, unread: 0 }
            : c
        )
      );
    } catch (e) {
      console.error('Erro ao enviar mensagem', e);
    }
  };

  const conversationStatusToApi = (v) => {
    if (v === CONVERSATION_STATUS.AGUARDANDO) return 'waiting';
    if (v === CONVERSATION_STATUS.FINALIZADO) return 'closed';
    return 'open';
  };

  const handleUpdateConversation = async (convId, updates) => {
    const hasApiUpdate =
      updates.conversationStatus !== undefined ||
      updates.stage !== undefined ||
      updates.teamId !== undefined ||
      updates.assignedAiId !== undefined ||
      updates.aiActive !== undefined;
    if (hasApiUpdate) {
      try {
        const payload = {};
        if (updates.conversationStatus !== undefined) payload.status = conversationStatusToApi(updates.conversationStatus);
        if (updates.stage !== undefined) payload.stage = updates.stage;
        if (updates.teamId !== undefined) payload.teamId = updates.teamId;
        if (updates.assignedAiId !== undefined) payload.assignedAiId = updates.assignedAiId;
        if (updates.aiActive !== undefined) payload.aiActive = updates.aiActive;
        const res = await apiFetch(`/conversations/${convId}`, {
          method: 'PATCH',
          json: payload,
        });
        const raw = res.data ?? res;
        const contactIndex = contacts.reduce((acc, c) => { acc[c.id] = c; return acc; }, {});
        const channelIndex = channels.reduce((acc, ch) => {
          acc[ch.id] = ch.label || ch.name || ch.type || ch.id;
          return acc;
        }, {});
        const updated = mapConversation(raw, contactIndex, channelIndex);
        setConversations((prev) => prev.map((c) => (c.id === convId ? { ...c, ...updated } : c)));
      } catch (e) {
        console.error('Erro ao atualizar conversa', e);
      }
      return;
    }
    setConversations((prev) =>
      prev.map((c) => (c.id === convId ? { ...c, ...updates } : c))
    );
  };

  const toggleAi = (id) => {
    const conv = conversations.find((c) => c.id === id);
    if (!conv) return;
    handleUpdateConversation(id, { aiActive: !conv.aiActive });
  };
  const handleAssignAi = (convId, aiId) => handleUpdateConversation(convId, { assignedAiId: aiId, aiActive: true });
  const handleStageChange = (convId, stageId) => handleUpdateConversation(convId, { stage: stageId });

  const handleDeleteConversation = async (convId) => {
    if (!convId) return;
    try {
      await apiFetch(`/conversations/${convId}`, { method: 'DELETE' });
      setConversations((prev) => prev.filter((c) => c.id !== convId));
      if (selectedId === convId) setSelectedId(null);
      setIsRightPanelOpen(false);
    } catch (e) {
      console.error('Erro ao excluir conversa', e);
    }
  };

  const handleUpdateContact = useUpdateContact(setContacts, setConversations);

  const handleCreateConversation = async (contactId, source) => {
    const contact = contacts.find((c) => c.id === contactId);
    if (!contact || !source) return;
    const channelId =
      (channels || []).find((ch) => ch.id === source)?.id ??
      (channels || []).find(
        (ch) => (ch.type || ch.name || ch.id || '').toLowerCase() === String(source).toLowerCase()
      )?.id;
    if (!channelId) {
      console.error('Canal não encontrado para:', source);
      return;
    }

    // Se for WhatsApp/Evolution, garantir que o contato tem telefone e escolher uma conta de canal.
    const channel = (channels || []).find((ch) => ch.id === channelId);
    const isWhatsAppEvolution =
      String(channel?.type || '').toUpperCase() === 'WHATSAPP' &&
      String(channel?.provider || '').toUpperCase() === 'EVOLUTION';
    if (isWhatsAppEvolution) {
      const phone = (contact.telefone ?? contact.phone ?? '').trim();
      if (!phone) {
        showToast({ message: 'Para WhatsApp, o contato precisa ter telefone.', type: 'error' });
        return;
      }
    }

    const channelAccountsForChannel = (channelAccounts || []).filter(
      (a) => a.channelId === channelId && String(a.status || 'active').toLowerCase() === 'active'
    );
    const preferredAccountId =
      isWhatsAppEvolution && channelAccountsForChannel.length ? channelAccountsForChannel[0].id : undefined;
    try {
      const res = await apiFetch('/conversations', {
        method: 'POST',
        json: { contactId, channelId, ...(preferredAccountId ? { channelAccountId: preferredAccountId } : {}) },
      });
      const raw = res.data ?? res;
      const contactIndex = contacts.reduce((acc, c) => { acc[c.id] = c; return acc; }, {});
      const channelIndex = (channels || []).reduce((acc, ch) => {
        acc[ch.id] = ch.label || ch.name || ch.type || ch.id;
        return acc;
      }, {});
      const newConv = mapConversation(raw, contactIndex, channelIndex);
      setConversations((prev) => [...prev, newConv]);
      setSelectedId(newConv.id);
      setShowCreateConversationModal(false);
    } catch (e) {
      console.error('Erro ao criar conversa', e);
    }
  };

  const handleSaveContact = async (data, isNew) => {
    const name = data.nome?.trim() || data.name?.trim() || '';
    if (!name) {
      showToast({ message: 'Informe o nome do contato.', type: 'error' });
      throw new Error('Nome obrigatório');
    }
    const tagsPayload =
      data.tags !== undefined ? (Array.isArray(data.tags) ? data.tags : []) : undefined;
    const payload = {
      name,
      phone: data.telefone?.trim() || data.phone?.trim() || undefined,
      email: data.email?.trim() || undefined,
    };
    if (tagsPayload !== undefined) payload.tags = tagsPayload;
    try {
      if (isNew) {
        const res = await apiFetch('/contacts', { method: 'POST', json: payload });
        const data = res.data ?? res;
        const created = data.contact ?? data;
        const wasNew = !data.updated;
        const contactForState = {
          ...created,
          nome: created.nome ?? created.name,
          telefone: created.telefone ?? created.phone,
          tags: Array.isArray(created.tags) ? created.tags : (tagsPayload ?? []),
        };
        setContacts((prev) => {
          const idx = prev.findIndex((c) => c.id === created.id);
          if (idx >= 0) return prev.map((c, i) => (i === idx ? contactForState : c));
          return [...prev, contactForState];
        });
        if (wasNew) setContactsTotal((t) => (t ?? 0) + 1);
        showToast({ message: 'Contato salvo com sucesso.', type: 'success' });
        try {
          await fetchContactsPage({ page: 1, pageSize: 10 });
        } catch (_) {
          // Lista já atualizada acima
        }
      } else {
        const res = await apiFetch(`/contacts/${data.id}`, { method: 'PATCH', json: payload });
        const updated = res.data ?? res;
        const normalizedTags =
          Array.isArray(updated.tags) ? updated.tags : (tagsPayload !== undefined ? tagsPayload : undefined);
        const contactForState = {
          ...updated,
          nome: updated.nome ?? updated.name,
          telefone: updated.telefone ?? updated.phone,
          ...(normalizedTags !== undefined ? { tags: normalizedTags } : {}),
        };
        setContacts((prev) => prev.map((c) => (c.id === data.id ? { ...c, ...contactForState } : c)));
        showToast({ message: 'Contato atualizado com sucesso.', type: 'success' });
      }
    } catch (e) {
      const msg = e?.message || 'Erro ao salvar contato. Tente novamente.';
      showToast({ message: msg, type: 'error' });
      throw e;
    }
  };

  const handleBulkUpdateContacts = async (contactIds, updates) => {
    const payload = {};
    if (updates.name !== undefined || updates.nome !== undefined) payload.name = (updates.name ?? updates.nome)?.trim() ?? '';
    if (updates.phone !== undefined || updates.telefone !== undefined) payload.phone = (updates.phone ?? updates.telefone)?.trim() ?? undefined;
    if (updates.email !== undefined) payload.email = updates.email?.trim() ?? undefined;
    if (Object.keys(payload).length === 0) {
      setContacts((prev) =>
        prev.map((c) => (contactIds.includes(c.id) ? { ...c, ...updates } : c))
      );
      return;
    }
    try {
      await Promise.all(
        contactIds.map((id) => apiFetch(`/contacts/${id}`, { method: 'PATCH', json: payload }))
      );
      setContacts((prev) =>
        prev.map((c) => {
          if (!contactIds.includes(c.id)) return c;
          const u = { ...c, ...updates, nome: payload.name ?? c.nome ?? c.name, telefone: payload.phone ?? c.telefone ?? c.phone, email: payload.email ?? c.email };
          return u;
        })
      );
    } catch (e) {
      console.error('Erro ao atualizar contatos em massa', e);
    }
  };

  const handleSaveCompany = async (data) => {
    const nomeFantasia = (data.nome_fantasia || '').trim();
    if (!nomeFantasia) {
      showToast({ message: 'Informe o nome fantasia da empresa.', type: 'error' });
      throw new Error('Nome fantasia obrigatório');
    }
    const payload = {
      nome_fantasia: nomeFantasia,
      razao_social: data.razao_social?.trim() || null,
      cnpj: data.cnpj?.trim() || null,
      observacoes_internas: data.observacoes_internas?.trim() || '',
      status: data.status || 'ativo',
    };
    try {
      if (data.id && companies.find((c) => c.id === data.id)) {
        const res = await apiFetch(`/companies/${data.id}`, { method: 'PATCH', json: payload });
        const updated = res.data ?? res;
        setCompanies((prev) => prev.map((c) => (c.id === data.id ? { ...c, ...updated } : c)));
        showToast({ message: 'Empresa atualizada com sucesso.', type: 'success' });
        return updated;
      }
      const res = await apiFetch('/companies', { method: 'POST', json: payload });
      const created = res.data ?? res;
      setCompanies((prev) => [...prev, created]);
      showToast({ message: 'Empresa criada com sucesso.', type: 'success' });
      return created;
    } catch (e) {
      const msg = e?.message || 'Erro ao salvar empresa. Tente novamente.';
      showToast({ message: msg, type: 'error' });
      throw e;
    }
  };

  const handleArchiveCompany = async (companyId, archive) => {
    try {
      const res = await apiFetch(`/companies/${companyId}`, {
        method: 'PATCH',
        json: { status: archive ? 'arquivado' : 'ativo' },
      });
      setCompanies((prev) => prev.map((c) => (c.id === companyId ? { ...c, ...res.data } : c)));
    } catch (e) {
      console.error('Erro ao arquivar empresa', e);
    }
  };

  const handleSaveUser = async (data, isNew) => {
    const payload = {
      name: data.name,
      email: data.email,
      role: toApiRole(data.role),
      teamId: data.teamId || null,
    };
    if (data.password?.trim()) payload.password = data.password.trim();
    try {
      if (isNew) {
        const res = await apiFetch('/users', {
          method: 'POST',
          json: payload,
        });
        const normalized = mapUser(res.data);
        setUsers((prev) => [...prev, normalized]);
        if (res.data?.tempPassword) {
          window.alert(`Senha temporária do usuário ${normalized.email}: ${res.data.tempPassword}`);
        }
        showToast({ message: 'Usuário criado com sucesso.', type: 'success' });
      } else {
        const res = await apiFetch(`/users/${data.id}`, { method: 'PATCH', json: payload });
        const normalized = mapUser(res.data);
        setUsers((prev) => prev.map((u) => (u.id === data.id ? normalized : u)));
        showToast({ message: 'Usuário atualizado com sucesso.', type: 'success' });
      }
    } catch (e) {
      console.error('Erro ao salvar usuário', e);
      showToast({ message: e?.message || e?.response?.data?.message || 'Erro ao salvar usuário.', type: 'error' });
      throw e;
    }
  };

  const handleDeleteUser = async (userId) => {
    console.warn('Remoção de usuário não está disponível no backend. Removendo apenas do estado local.');
    setUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const handleSaveTag = async (tag, isNew) => {
    try {
      if (isNew) {
        const res = await apiFetch('/tags', { method: 'POST', json: { name: tag.name, slug: tag.slug || undefined, color: tag.color, border: tag.border } });
        setTags((prev) => [...prev, res.data]);
      } else {
        const res = await apiFetch(`/tags/${tag.id}`, { method: 'PATCH', json: { name: tag.name, slug: tag.slug || undefined, color: tag.color, border: tag.border } });
        setTags((prev) => prev.map((t) => (t.id === tag.id ? { ...t, ...res.data } : t)));
      }
    } catch (e) {
      console.error('Erro ao salvar tag', e);
      const msg = e.status === 403
        ? 'Você não tem permissão para esta edição. Apenas administradores podem alterar estas configurações.'
        : (e.status === 409 || (e.message && (e.message.includes('Slug') || e.message.includes('já em uso')))
          ? 'Este slug já está em uso. Escolha outro.'
          : (e.message || 'Erro ao salvar tag.'));
      showToast({ message: msg, type: 'error' });
      throw e;
    }
  };

  const handleDeleteTag = async (id) => {
    try {
      await apiFetch(`/tags/${id}`, { method: 'DELETE' });
      setTags((prev) => prev.filter((t) => t.id !== id));
    } catch (e) {
      console.error('Erro ao remover tag', e);
      const msg = e.status === 403
        ? 'Você não tem permissão para esta edição. Apenas administradores podem alterar estas configurações.'
        : (e.message || 'Erro ao remover tag.');
      showToast({ message: msg, type: 'error' });
    }
  };

  const normalizeTeam = (raw) => ({
    id: raw?.id,
    name: raw?.name,
    slug: raw?.slug ?? '',
    color: raw?.color ?? 'bg-slate-100 text-slate-800',
    border: raw?.border ?? 'border-slate-200',
  });

  const handleSaveTeam = async (team, isNew) => {
    try {
      if (isNew) {
        const res = await apiFetch('/teams', { method: 'POST', json: { name: team.name, slug: team.slug || undefined, color: team.color, border: team.border } });
        const data = res?.data ?? res;
        setTeams((prev) => [...prev, normalizeTeam(data)]);
        showToast({ message: 'Equipe salva com sucesso.', type: 'success' });
      } else {
        const res = await apiFetch(`/teams/${team.id}`, { method: 'PATCH', json: { name: team.name, slug: team.slug || undefined, color: team.color, border: team.border } });
        const data = res?.data ?? res;
        setTeams((prev) => prev.map((t) => (t.id === team.id ? normalizeTeam({ ...t, ...data }) : t)));
        showToast({ message: 'Equipe salva com sucesso.', type: 'success' });
      }
    } catch (e) {
      console.error('Erro ao salvar equipe', e);
      const isSlugConflict = e.status === 409 || (e.message && (e.message.includes('Slug') || e.message.includes('já em uso')));
      showToast({ message: isSlugConflict ? 'Este slug já está em uso. Escolha outro.' : (e.message || 'Erro ao salvar equipe.'), type: 'error' });
      throw e;
    }
  };

  const handleDeleteTeam = async (id) => {
    try {
      await apiFetch(`/teams/${id}`, { method: 'DELETE' });
      setTeams((prev) => prev.filter((t) => t.id !== id));
    } catch (e) {
      console.error('Erro ao remover equipe', e);
    }
  };

  const handleSaveStage = async (stage, isNew) => {
    try {
      if (isNew) {
        const res = await apiFetch('/funnel-stages', { method: 'POST', json: toApiStage(stage) });
        setFunnelStages((prev) => [...prev, fromApiStage(res.data)]);
      } else {
        const res = await apiFetch(`/funnel-stages/${stage.id}`, { method: 'PATCH', json: toApiStage(stage) });
        setFunnelStages((prev) => prev.map((s) => (s.id === stage.id ? fromApiStage(res.data) : s)));
      }
    } catch (e) {
      console.error('Erro ao salvar etapa', e);
      const msg = e.status === 403
        ? 'Você não tem permissão para esta edição. Apenas administradores podem alterar estas configurações.'
        : (e.status === 409 || (e.message && (e.message.includes('Slug') || e.message.includes('já em uso')))
          ? 'Este slug já está em uso. Escolha outro.'
          : (e.message || 'Erro ao salvar etapa.'));
      showToast({ message: msg, type: 'error' });
      throw e;
    }
  };

  const handleDeleteStage = async (id) => {
    try {
      await apiFetch(`/funnel-stages/${id}`, { method: 'DELETE' });
      setFunnelStages((prev) => prev.filter((s) => s.id !== id));
    } catch (e) {
      console.error('Erro ao remover etapa', e);
      const msg = e.status === 403
        ? 'Você não tem permissão para esta edição. Apenas administradores podem alterar estas configurações.'
        : (e.message || 'Erro ao remover etapa.');
      showToast({ message: msg, type: 'error' });
    }
  };

  const handleReorderStages = async (nextStages) => {
    setFunnelStages(nextStages);
    try {
      await Promise.all(
        nextStages.map((stage) =>
          apiFetch(`/funnel-stages/${stage.id}`, { method: 'PATCH', json: toApiStage(stage) })
        )
      );
    } catch (e) {
      console.error('Erro ao reordenar etapas', e);
      const msg = e.status === 403
        ? 'Você não tem permissão para esta edição. Apenas administradores podem alterar estas configurações.'
        : (e.message || 'Erro ao reordenar etapas.');
      showToast({ message: msg, type: 'error' });
    }
  };

  const handleSaveSla = async (config) => {
    try {
      await apiFetch('/sla-config', { method: 'PATCH', json: { config } });
      setSlaConfig(config);
    } catch (e) {
      console.error('Erro ao salvar SLA', e);
      const msg = e.status === 403
        ? 'Você não tem permissão para esta edição. Apenas administradores podem alterar estas configurações.'
        : (e.message || 'Erro ao salvar SLA.');
      showToast({ message: msg, type: 'error' });
    }
  };

  useEffect(() => {
    const lastMsg = selectedConvWithMessages?.messages?.[selectedConvWithMessages.messages.length - 1];
    if (selectedConvWithMessages?.aiActive && lastMsg?.sender === 'customer') {
      const timer = setTimeout(() => {
        const agent =
          aiAgents.find(a => a.id === selectedConvWithMessages.assignedAiId) ||
          aiAgents.find(a => a.teamId === selectedConvWithMessages.teamId);

        if (!agent) return;

        const response = `Com base no conhecimento de "${agent.internalName}", identifiquei que posso ajudar. Gostaria de continuar?`;
        handleSendMessage(selectedConvWithMessages.id, response, true, agent.publicName);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [
    selectedConvWithMessages?.messages,
    selectedConvWithMessages?.aiActive,
    selectedConvWithMessages?.assignedAiId,
    selectedConvWithMessages?.teamId,
    aiAgents,
  ]);

  return (
    <div className="flex h-screen bg-appPage font-sans overflow-hidden antialiased text-appText fixed inset-0 select-none">
      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={handleTabChange} 
        currentUser={currentProfile} 
        onSwitchUser={setCurrentProfile} 
        onLogout={logout}
        users={users}
      />

      <main className="flex-1 flex overflow-hidden">
        {activeTab === 'inbox' ? (
          <>
            <ConversationList 
              conversations={conversationsForList} 
              selectedId={selectedId} 
              onSelect={setSelectedId}
              teams={teamsAsObject}
              funnelStages={funnelStages}
              statusFilter={messagesStatusFilter}
              onStatusFilterChange={setMessagesStatusFilter}
              searchQuery={messagesSearchQuery}
              onSearchQueryChange={setMessagesSearchQuery}
              teamFilterId={messagesTeamFilterId}
              onTeamFilterIdChange={setMessagesTeamFilterId}
              channelFilter={messagesChannelFilter}
              onChannelFilterChange={setMessagesChannelFilter}
              tagFilterId={messagesTagFilterId}
              onTagFilterIdChange={setMessagesTagFilterId}
              tags={tags}
              onNewConversation={() => setShowCreateConversationModal(true)}
            />
            <ChatWindow 
              conversation={selectedConvWithMessages} 
              currentUser={currentProfile}
              onSendMessage={handleSendMessage} 
              aiAgents={aiAgents}
              toggleAi={toggleAi}
              onAssignAi={handleAssignAi}
              onInfoClick={() => setIsRightPanelOpen(v => !v)}
              onUpdateConversation={handleUpdateConversation}
            />
            {isRightPanelOpen && (
              <RightProfilePanel
                conversation={selectedConvWithMessages}
                contact={selectedContact}
                companies={companies}
                tags={tags}
                funnelStages={funnelStages}
                teams={teamsAsObject}
                onUpdateConversation={handleUpdateConversation}
                onUpdateContact={handleUpdateContact}
                onClose={() => setIsRightPanelOpen(false)}
                onDeleteConversation={handleDeleteConversation}
                isAdmin={currentProfile?.role === 'admin'}
              />
            )}
            {showCreateConversationModal && (
              <CreateConversationModal
                open={showCreateConversationModal}
                onClose={() => setShowCreateConversationModal(false)}
                channels={channels.map((ch) => ({ id: ch.id, label: ch.label || ch.type || ch.id }))}
                contacts={contacts}
                conversations={conversations}
                getContactForConversation={getContactForConversation}
                onCreateConversation={handleCreateConversation}
              />
            )}
          </>
        ) : activeTab === 'contacts' ? (
          <ContactsArea
            contacts={contacts}
            companies={companies}
            tags={tags}
            onSaveContact={handleSaveContact}
            onBulkUpdateContacts={handleBulkUpdateContacts}
            onSaveCompany={handleSaveCompany}
            onArchiveCompany={handleArchiveCompany}
            onFetchContacts={fetchContactsPage}
            onFetchCompanies={fetchCompaniesPage}
            contactsTotal={contactsTotal}
            companiesTotal={companiesTotal}
            subTab={contactsSubTab}
            onSubTabChange={(tab) => navigate(tab === 'companies' ? '/contatos/empresas' : '/contatos')}
          />
        ) : activeTab === 'funnel' ? (
          <ConversationFunnel
            conversations={conversationsForFunnel}
            funnelStages={funnelStages}
            teams={teamsAsObject}
            contacts={contacts}
            onSelectConversation={(id) => { setSelectedId(id); navigate('/inbox'); }}
            onStageChange={handleStageChange}
            searchQuery={funnelSearchQuery}
            onSearchQueryChange={setFunnelSearchQuery}
            teamFilterId={funnelTeamFilterId}
            onTeamFilterIdChange={setFunnelTeamFilterId}
            channelFilter={funnelChannelFilter}
            onChannelFilterChange={setFunnelChannelFilter}
            tagFilterId={funnelTagFilterId}
            onTagFilterIdChange={setFunnelTagFilterId}
            tags={tags}
            statusFilter={funnelStatusFilter}
            onStatusFilterChange={setFunnelStatusFilter}
          />
        ) : activeTab === 'ai_config' ? (
          <AiConfigPanel
            agents={aiAgents}
            selectedAgentId={aiSelectedAgentId}
            setSelectedAgentId={setAiSelectedAgentId}
            selectedAgent={aiSelectedAgent}
            createAgent={createAgent}
            updateAgent={updateAgent}
            deleteAgent={deleteAgent}
            addKnowledgeSource={addKnowledgeSource}
            removeKnowledgeSource={removeKnowledgeSource}
            addIntegration={addIntegration}
            setIntegrationStatus={setIntegrationStatus}
            removeIntegration={removeIntegration}
            teamsMap={teamsAsObject}
          />
        ) : activeTab === 'settings' ? (
          <SettingsPanel
            funnelStages={funnelStages}
            onSaveStage={handleSaveStage}
            onDeleteStage={handleDeleteStage}
            onReorderStages={handleReorderStages}
            onToast={showToast}
            users={users}
            onSaveUser={handleSaveUser}
            onDeleteUser={handleDeleteUser}
            teams={teams}
            onSaveTeam={handleSaveTeam}
            onDeleteTeam={handleDeleteTeam}
            tags={tags}
            onSaveTag={handleSaveTag}
            onDeleteTag={handleDeleteTag}
            slaConfig={slaConfig}
            onSaveSla={handleSaveSla}
          />
        ) : activeTab === 'analytics' ? (
          <ReportsPanel conversations={conversations} slaConfig={slaConfig} />
        ) : (
          <div className="flex-1 flex items-center justify-center bg-appSurface m-8 rounded-[40px] shadow-sm border border-appSurfaceBorder">
            <div className="text-center">
              <LayoutGrid size={48} className="mx-auto text-appTextMuted mb-4" />
              <h2 className="text-xl font-bold text-appText">Em Desenvolvimento</h2>
              <p className="text-appTextMuted text-sm">Este módulo estará disponível em breve.</p>
            </div>
          </div>
        )}
      </main>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}
